import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "store/Store";
import { StringRegistryItem, StringRegistryFilters } from "../types/stringRegistryTypes";
import { UISchemaResponse } from "features/config-registry/types/configRegistryTypes";

const selectSlice = (state: RootState) => (state as any).stringRegistryUI;

interface PaginationMeta {
  page: number;
  pageSize: number;
  pageCount: number;
  total: number;
}

const defaultFilters: StringRegistryFilters = {
  search: "",
  verificationStatus: "",
  component: "",
  section: ""
};

const initialState = {
  strings: [] as StringRegistryItem[],
  pagination: { page: 1, pageSize: 25, pageCount: 1, total: 0 } as PaginationMeta,
  filters: defaultFilters,
  isLoading: false,
  error: undefined as string | undefined,
  isFilterPanelOpen: false,
  uiSchema: null as UISchemaResponse | null,
};

export const stringRegistryUISlice = createSlice({
  name: "stringRegistryUI",
  initialState,
  reducers: {
    setStrings: (state, action: PayloadAction<StringRegistryItem[]>) => {
      state.strings = action.payload;
    },
    setPagination: (state, action: PayloadAction<PaginationMeta>) => {
      state.pagination = action.payload;
    },
    setFilters: (state, action: PayloadAction<Partial<StringRegistryFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    resetFilters: (state) => {
      state.filters = defaultFilters;
    },
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string | undefined>) => {
      state.error = action.payload;
    },
    toggleFilterPanel: (state) => {
      state.isFilterPanelOpen = !state.isFilterPanelOpen;
    },
    setFilterPanelOpen: (state, action: PayloadAction<boolean>) => {
      state.isFilterPanelOpen = action.payload;
    },
    setUISchema: (state, action: PayloadAction<UISchemaResponse>) => {
      state.uiSchema = action.payload;
    },
  }
});

export const {
  setStrings,
  setPagination,
  setFilters,
  resetFilters,
  setIsLoading,
  setError,
  toggleFilterPanel,
  setFilterPanelOpen,
  setUISchema,
} = stringRegistryUISlice.actions;

export const selectStringRegistryStrings = (state: RootState) =>
  (selectSlice(state)?.strings ?? []) as StringRegistryItem[];
export const selectStringRegistryPagination = (state: RootState) =>
  selectSlice(state)?.pagination ?? { page: 1, pageSize: 25, pageCount: 1, total: 0 };
export const selectStringRegistryFilters = (state: RootState) =>
  selectSlice(state)?.filters ?? defaultFilters;
export const selectStringRegistryIsLoading = (state: RootState) =>
  selectSlice(state)?.isLoading ?? false;
export const selectStringRegistryError = (state: RootState) => selectSlice(state)?.error;
export const selectIsFilterPanelOpen = (state: RootState) =>
  selectSlice(state)?.isFilterPanelOpen ?? false;
export const selectUISchema = (state: RootState) =>
  selectSlice(state)?.uiSchema as UISchemaResponse | null;
export const selectHasActiveFilters = (state: RootState) => {
  const f = selectSlice(state)?.filters ?? defaultFilters;
  return !!(f.search || f.verificationStatus || f.component || f.section);
};
